import { useLayoutEffect, useState } from 'react'
import { getVisibleColumnRange } from './getVisibleColumnRange'

export const useVisibleColumnRange = ({
  columnCount,
  columnWidth,
  initialViewportWidth,
  offsetStart,
  overscan,
  renderAllUpTo,
  scrollElementRef,
}: {
  columnCount: number
  columnWidth: number
  initialViewportWidth: number
  offsetStart: number
  overscan: number
  renderAllUpTo: number
  scrollElementRef: React.RefObject<HTMLElement | null>
}) => {
  const [range, setRange] = useState(() =>
    getVisibleColumnRange({
      columnCount,
      columnWidth,
      offsetStart,
      overscan,
      renderAllUpTo,
      scrollLeft: 0,
      viewportWidth: initialViewportWidth,
    }),
  )

  useLayoutEffect(() => {
    const scrollElement = scrollElementRef.current

    if (!scrollElement) return

    let frame: number | null = null

    const updateRange = () => {
      // a hidden grid has no width, and its range would only change back when it is shown again
      if (scrollElement.clientWidth === 0) return

      const nextRange = getVisibleColumnRange({
        columnCount,
        columnWidth,
        offsetStart,
        overscan,
        renderAllUpTo,
        scrollLeft: scrollElement.scrollLeft,
        viewportWidth: scrollElement.clientWidth,
      })

      setRange((previousRange) =>
        previousRange.startIndex === nextRange.startIndex && previousRange.endIndex === nextRange.endIndex
          ? previousRange
          : nextRange,
      )
    }

    const updateRangeOnNextFrame = () => {
      if (frame !== null) return

      frame = window.requestAnimationFrame(() => {
        frame = null

        updateRange()
      })
    }

    updateRange()

    scrollElement.addEventListener('scroll', updateRangeOnNextFrame, { passive: true })

    const resizeObserver = new ResizeObserver(updateRangeOnNextFrame)

    resizeObserver.observe(scrollElement)

    return () => {
      scrollElement.removeEventListener('scroll', updateRangeOnNextFrame)

      resizeObserver.disconnect()

      if (frame !== null) window.cancelAnimationFrame(frame)
    }
  }, [columnCount, columnWidth, offsetStart, overscan, renderAllUpTo, scrollElementRef])

  return range
}
